import { useCallback, type Dispatch, type SetStateAction } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { AdminCapability, AdminRole, AdminSection, AdminUser } from '../../types'
import { fetchTeamSnapshot } from './securityTeamApi'
import { runAsyncAction } from './asyncActionFeedback'

type Translator = (key: string, params?: Record<string, string | number>) => string

type TeamStateSetters = {
  setUsers: Dispatch<SetStateAction<AdminUser[]>>
  setRoles: Dispatch<SetStateAction<AdminRole[]>>
  setCapabilities: Dispatch<SetStateAction<AdminCapability[]>>
  setSections: Dispatch<SetStateAction<AdminSection[]>>
  setTeamLoading: Dispatch<SetStateAction<boolean>>
}

export const useSecurityTeamLoader = ({
  token,
  authHeaders,
  t,
  setters,
}: {
  token: string | null
  authHeaders: (withJson?: boolean) => Record<string, string>
  t: Translator
  setters: TeamStateSetters
}) =>
  useCallback(async () => {
    if (!token) return
    const snapshot = await runAsyncAction({
      action: () => fetchTeamSnapshot({ authHeaders, t }),
      fallbackErrorMessage: t('security.team.loadError'),
      setLoading: setters.setTeamLoading,
      onErrorMessage: (message) => toast.error(message),
    })
    if (!snapshot) return

    setters.setUsers(snapshot.users)
    setters.setRoles(snapshot.roles)
    setters.setCapabilities(snapshot.capabilities)
    setters.setSections(snapshot.sections)
  }, [authHeaders, setters, t, token])
